import React, { useEffect, useState, useRef, memo } from 'react';
import { Tag, ShoppingCart, Heart, Clock, Search, Percent, Bookmark, Image, Star, Users, Gift, Sparkles, Crown } from 'lucide-react';
const features = [
// FREE TOOLS (100% free)
{
  icon: <Heart className="h-6 w-6" data-id="element-71" />,
  title: 'Wishlist System',
  description: 'Capture "maybe later" shoppers and bring them back. 31% of wishlist users return to buy within 30 days. Perfect for gift registries and high-ticket items.',
  gradient: 'from-pink-500 to-red-500',
  badge: 'Free',
  badgeType: 'free',
  features: ['Guest & logged-in wishlists', 'Beautiful heart/bookmark icons', 'Dedicated wishlist page', 'AJAX add/remove']
}, {
  icon: <ShoppingCart className="h-6 w-6" data-id="element-72" />,
  title: 'Side Cart Drawer',
  description: 'Stop losing 70% of shoppers to forgotten carts. Slide-out cart that follows customers everywhere, making checkout just one click away. Cuts abandonment by 28%.',
  gradient: 'from-green-500 to-emerald-500',
  badge: 'Free',
  badgeType: 'free',
  features: ['Slide-out drawer animation', 'Live quantity updates', 'Menu integration triggers', 'Mobile-optimized design']
},
// LIMITED FREE TOOLS
{
  icon: <Tag className="h-6 w-6" data-id="element-73" />,
  title: 'Product Badge System',
  description: 'Grab attention instantly with eye-catching badges. "New", "Sale", "Hot", "Limited"—create urgency that drives clicks and conversions. Auto-assign by rules or manually place on any product.',
  gradient: 'from-purple-500 to-pink-500',
  badge: 'Free: 2 badges',
  badgeType: 'limited',
  features: ['Unlimited custom badge designs', 'Auto-badge new products & sales', 'Corner positioning & animations', 'Bulk assignment tools']
}, {
  icon: <Percent className="h-6 w-6" data-id="element-74" />,
  title: 'Multi-Item Discounts',
  description: 'Turn single buyers into bulk purchasers. "Buy 3, Save 15%"—tiered pricing that rewards larger orders and boosts your average cart value by 42% on autopilot.',
  gradient: 'from-blue-500 to-cyan-500',
  badge: 'Free: 2 rules',
  badgeType: 'limited',
  features: ['Unlimited discount tiers', 'Product or category-wide deals', 'Real-time savings display', 'Stackable promotions']
}, {
  icon: <Image className="h-6 w-6" data-id="element-75" />,
  title: 'Product Gallery & Video',
  description: 'Turn product images into conversion machines. Feature image sliders on listings, video galleries on product pages. Video increases conversions by 80%.',
  gradient: 'from-violet-500 to-purple-500',
  badge: 'Gallery free',
  badgeType: 'limited',
  features: ['Feature image slider', 'Product video support', 'Video on listings or gallery', 'Responsive & fast-loading']
},
// PREMIUM TOOLS
{
  icon: <Sparkles className="h-6 w-6" data-id="element-76" />,
  title: 'Store-Wide Flash Sales',
  description: 'Launch campaigns that create buying frenzies. Countdown timers, sale banners, and scheduled promotions that trigger FOMO and drive 35% sales spikes during events.',
  gradient: 'from-red-500 to-orange-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Countdown timers with 6 themes', 'Sticky sale banners', 'Timezone-aware scheduling', 'Minimum price protection']
}, {
  icon: <Search className="h-6 w-6" data-id="element-77" />,
  title: 'AJAX Instant Search',
  description: 'Show results before customers finish typing. Lightning-fast search with live previews, filters, and "Add to Cart" from results. Find products 45% faster = more sales.',
  gradient: 'from-indigo-500 to-purple-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['6 beautiful search templates', 'Search by title, SKU, content', 'Split preview with hover', 'Mobile-optimized interface']
}, {
  icon: <Clock className="h-6 w-6" data-id="element-78" />,
  title: 'Recently Viewed Products',
  description: 'Keep browsers engaged with smart product memory. Show what caught their eye—they browse 24% longer and discover more. Perfect for "pick up where you left off" experiences.',
  gradient: 'from-orange-500 to-yellow-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Auto-tracks up to 20 products', 'Responsive grid layouts', 'Shortcode for any page', 'Cookie-based (GDPR-friendly)']
}, {
  icon: <Bookmark className="h-6 w-6" data-id="element-79" />,
  title: 'Save for Later (Cart)',
  description: 'Amazon-style "Save for Later" on cart page. Let customers move items aside without losing them. Reduces cart abandonment and keeps shoppers engaged longer.',
  gradient: 'from-teal-500 to-green-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Move cart items to "saved"', 'Separate saved section', 'Quick add back to cart', 'Persistent across sessions']
}, {
  icon: <Star className="h-6 w-6" data-id="element-80" />,
  title: 'Advanced Product Reviews',
  description: 'Collect reviews that sell. Scheduled email campaigns, photo & video reviews, slider/grid displays. Social proof that converts browsers into buyers.',
  gradient: 'from-yellow-500 to-orange-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Automated review requests', 'Photo & video reviews', 'Multiple display layouts', 'Review moderation tools']
}, {
  icon: <Users className="h-6 w-6" data-id="element-81" />,
  title: 'Wholesale Pricing',
  description: 'Unlock B2B revenue streams. Custom user role with wholesale prices, separate from retail. Perfect for distributors, bulk buyers, and trade customers.',
  gradient: 'from-blue-600 to-indigo-600',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Wholesale user role', 'Product-level pricing', 'Hide retail prices', 'Bulk order management']
}, {
  icon: <Gift className="h-6 w-6" data-id="element-82" />,
  title: 'Digital Gift Cards',
  description: 'Create a revenue stream that never expires. Sell gift cards that bring new customers and guaranteed future sales. Perfect for holidays and corporate gifting.',
  gradient: 'from-rose-500 to-pink-500',
  badge: 'Premium',
  badgeType: 'premium',
  features: ['Custom denominations', 'Email delivery system', 'Unique redemption codes', 'Balance tracking']
}];
export function Features() {
  const featuresRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }
    return () => {
      if (featuresRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  const getBadgeStyles = (badgeType: string) => {
    switch (badgeType) {
      case 'free':
        return 'bg-green-100 text-green-700';
      case 'limited':
        return 'bg-blue-100 text-blue-700';
      case 'premium':
        return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700';
      default:
        return 'bg-purple-100 text-purple-700';
    }
  };
  return <section id="features" className="py-20 md:py-32 bg-white" ref={featuresRef} data-id="element-83">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-84">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-85">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-86">
            12 Powerful Tools That{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent" data-id="element-87">
              Replace a Dozen Plugins
            </span>
          </h2>
          <p className="text-xl text-gray-600" data-id="element-88">
            Stop juggling multiple plugins. Get everything you need to boost
            conversions, increase order values, and create an Amazon-level
            shopping experience.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-89">
          {features.map((feature, index) => <div key={index} className={`group bg-white rounded-2xl p-8 transition-all duration-700 transform border border-gray-200 hover:border-gray-300 hover:shadow-2xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{
          transitionDelay: `${index * 100}ms`
        }} data-id="element-90">
              <div className="flex items-start justify-between mb-6" data-id="element-91">
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.gradient} group-hover:scale-110 transition-transform shadow-lg`} data-id="element-92">
                  <div className="text-white" data-id="element-93">{feature.icon}</div>
                </div>
                <span className={`text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1 ${getBadgeStyles(feature.badgeType)}`} data-id="element-94">
                  {feature.badgeType === 'premium' && <Crown className="h-3 w-3" data-id="element-95" />}
                  {feature.badge}
                </span>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-96">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6" data-id="element-97">
                {feature.description}
              </p>

              <ul className="space-y-2" data-id="element-98">
                {feature.features.map((feat, i) => <li key={i} className="flex items-center text-sm text-gray-700" data-id="element-99">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 mr-2" data-id="element-100"></div>
                    {feat}
                  </li>)}
              </ul>
            </div>)}
        </div>

        <div className="mt-20 text-center" data-id="element-101">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-full px-6 py-3" data-id="element-102">
            <Sparkles className="h-5 w-5 text-purple-600" data-id="element-103" />
            <span className="font-medium text-gray-900" data-id="element-104">
              5 tools FREE forever • 7 premium tools unlock everything
            </span>
          </div>
        </div>
      </div>
    </section>;
}